import type { NodeId, NodeMap } from '../types';
import type { GraphIndex } from '../graph';
import { COLLISION } from './constants';

/**
 * Separate overlapping nodes.
 *
 * The vanilla `resolveCollisions()` measures the DOM — `nodeBox()` reads `offsetWidth` and
 * `offsetHeight` — from INSIDE an O(n²) loop run up to 60 times, interleaving those reads
 * with writes to `nodes[].y`. It works only because width and height do not change when y
 * does, an invariant nothing enforces and nothing states.
 *
 * Here sizes arrive as a map. The 60-pass loop touches zero DOM, so it is deterministic,
 * trivially testable, and no longer forces a layout flush per iteration.
 *
 * Behaviour is preserved exactly, including the bits that look wrong:
 *
 * - The `dy` "fallback push" in the original is computed and never used; `delta` is what
 *   moves the node, and `delta` is `need` or 1. Reproduced as-is. Using `dy` instead would
 *   change how far nodes jump apart, which is a visual change, not a fix.
 * - Two nodes that overlap but are not separable (identical tops, both locked) fall through
 *   to a 1px nudge, which is what lets the pass counter terminate.
 */

export interface Box {
  left: number;
  right: number;
  top: number;
  bottom: number;
  w: number;
  h: number;
}

export interface CollisionInput {
  nodes: NodeMap;
  graph: GraphIndex;
  /** Current y per node — the thing being adjusted. Not mutated; a new map is returned. */
  positions: Record<NodeId, { x: number; y: number; side: number }>;
  /** Measured size per node. Missing entries fall back to the same defaults as `nodeBox()`. */
  sizeOf: (id: NodeId) => { w: number; h: number } | undefined;
  /** Nodes that take part. The original excludes hidden nodes and the root. */
  ids: NodeId[];
  /** Held fixed along with its whole subtree. Everything else moves around it. */
  anchorId?: NodeId | null;
}

function boxFor(
  id: NodeId,
  y: number,
  input: CollisionInput,
): Box {
  const n = input.nodes[id];
  const p = input.positions[id];
  const size = input.sizeOf(id);
  const w = size?.w ?? COLLISION.FALLBACK_W;
  const h = size?.h ?? COLLISION.FALLBACK_H;

  // Where the box sits relative to its x depends on the side, because a left-side node is
  // laid out right-aligned on its column and the root is centred on it.
  let left: number;
  if (n?.root) left = p.x - w / 2;
  else if ((n?.side || 1) >= 0) left = p.x;
  else left = p.x - w;

  return { left, right: left + w, top: y, bottom: y + h, w, h };
}

function overlaps(a: Box, b: Box, padX: number, padY: number): boolean {
  return (
    a.left < b.right + padX && a.right + padX > b.left && a.top < b.bottom + padY && a.bottom + padY > b.top
  );
}

/**
 * Returns the adjusted y for every id in `input.ids`. Nodes not in `ids` are untouched.
 *
 * A node moves with its whole subtree — separating a parent from its children would undo the
 * layout rather than tidy it.
 */
export function resolveCollisions(input: CollisionInput): Record<NodeId, number> {
  const { PADX, PADY, MAXPASS } = COLLISION;
  const ids = input.ids;

  // Working copy. The caller's positions are never mutated.
  const y: Record<NodeId, number> = {};
  for (const id of ids) y[id] = input.positions[id]?.y ?? 0;

  // The anchor and everything under it are locked together.
  const locked = new Set<NodeId>();
  if (input.anchorId) {
    locked.add(input.anchorId);
    for (const d of input.graph.descendants(input.anchorId)) locked.add(d);
  }

  // Subtrees are needed on every move; computing them once is the difference between
  // O(passes x n²) and O(passes x n² x n).
  const subtree = new Map<NodeId, NodeId[]>();
  const subtreeOf = (id: NodeId): NodeId[] => {
    let s = subtree.get(id);
    if (!s) {
      s = input.graph.descendants(id).filter((d) => d in y);
      subtree.set(id, s);
    }
    return s;
  };

  const shift = (id: NodeId, dy: number, boxes: Record<NodeId, Box>) => {
    y[id] += dy;
    boxes[id] = boxFor(id, y[id], input);
    for (const d of subtreeOf(id)) {
      y[d] += dy;
      if (boxes[d]) boxes[d] = boxFor(d, y[d], input);
    }
  };

  for (let pass = 0; pass < MAXPASS; pass++) {
    let moved = false;
    const boxes: Record<NodeId, Box> = {};
    for (const id of ids) boxes[id] = boxFor(id, y[id], input);

    for (let a = 0; a < ids.length; a++) {
      for (let b = a + 1; b < ids.length; b++) {
        const ia = ids[a];
        const ib = ids[b];
        if (!overlaps(boxes[ia], boxes[ib], PADX, PADY)) continue;

        const aFixed = !!input.anchorId && locked.has(ia);
        const bFixed = !!input.anchorId && locked.has(ib);
        if (aFixed && bFixed) continue; // same locked group

        let mover: NodeId;
        let other: NodeId;
        if (aFixed) {
          mover = ib;
          other = ia;
        } else if (bFixed) {
          mover = ia;
          other = ib;
        } else {
          // Otherwise the lower one gives way, so the tree keeps reading top-to-bottom.
          mover = boxes[ia].top <= boxes[ib].top ? ib : ia;
          other = mover === ia ? ib : ia;
        }

        const need = boxes[other].bottom + PADY - boxes[mover].top;
        // See the header note: the original's `dy` fallback is dead code, and `delta` is what
        // actually moves. The 1px floor is what guarantees the pass loop terminates.
        const delta = need > 0 ? need : 1;
        shift(mover, Math.max(delta, 1), boxes);
        moved = true;
      }
    }
    if (!moved) break;
  }

  return y;
}
